/**
 * Client-side password feedback — a courtesy, never the gate.
 *
 * The server (packages/auth) is the authority; these helpers only spare a doomed
 * round-trip and show the rules before the user is rejected. The one real rule is LENGTH:
 * the policy is deliberately length-only (NIST-style), rejecting composition rules that
 * measurably push people toward `Password1!`. So the strength meter is length-only too —
 * it does not invent "add a symbol" requirements the server does not enforce.
 */

/** Mirrors packages/auth PASSWORD_MIN_LENGTH. Web cannot import the server policy package. */
export const MIN_PASSWORD_LENGTH = 12;

export interface PasswordCheck {
  readonly longEnough: boolean;
  /** True only when a confirm value is supplied and equal. Callers without a confirm field pass the password itself. */
  readonly matches: boolean;
  /** Ready to submit: long enough AND (for screens that confirm) the entries agree. */
  readonly valid: boolean;
  /** 0 (empty) … 4 (excellent). Length-only, matching the policy's philosophy. */
  readonly strength: 0 | 1 | 2 | 3 | 4;
  readonly strengthLabel: string;
}

export function evaluatePassword(password: string, confirm: string): PasswordCheck {
  const longEnough = password.length >= MIN_PASSWORD_LENGTH;
  const matches = password.length > 0 && password === confirm;

  let strength: PasswordCheck["strength"] = 0;
  let strengthLabel = "";
  if (password.length === 0) {
    strength = 0;
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    strength = 1;
    strengthLabel = "Too short";
  } else if (password.length < 16) {
    strength = 2;
    strengthLabel = "Good";
  } else if (password.length < 20) {
    strength = 3;
    strengthLabel = "Strong";
  } else {
    strength = 4;
    strengthLabel = "Excellent";
  }

  return { longEnough, matches, valid: longEnough && matches, strength, strengthLabel };
}
